import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';
import map from 'lodash/map';
import produce from 'immer';
import reduce from 'lodash/reduce';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import {
  addNewNodeToConfiguration,
  insertTemplate,
} from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/newNodesHelper';
import baseIvrsResource from './baseIvrsResource';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import Ivr from 'src/models/Ivrs/Ivr';
import { IVR_TAGS } from 'src/models/Thread/Thread';
import { IvrConfiguration } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import IvrNode from 'src/models/Ivrs/IvrNode';
import ivrResource from './ivrResource';
import ivrsTreeDataResource, { convertCoordinatesInTreeDataToString } from './ivrsTreeDataResource';
import { logout } from 'src/actions/auth/authSlice';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import { START_NODE_ID } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { toastError, toastSuccess } from 'src/actions/toast';
import { translateErrors } from 'src/adapters/validateType';

const ivrsResource = baseIvrsResource<Ivr>({
  name: 'ivrs',
  endpoint: { fetchAllUrl: 'v1/orgs/:orgId/configuration/ivrs/:id' },
  type: Ivr,
});

ivrsResource.effects.fetchAll =
  (params: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(ivrsResource.actions.fetchStarted({}));
      const result = await gateways.ivrs.requestAll(params, { tag: [IVR_TAGS.TELEPHONY] });
      dispatch(ivrsResource.actions.fetchSuccess(result));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(ivrsResource.actions.fetchError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(ivrsResource.actions.fetchRequestError({}));
      }
    }
  };

const createNewIvr: sharedTypes.AppThunk =
  (newIvr: Ivr) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(addStarted({}));
      dispatch(ivrResource.actions.fetchStarted({}));
      dispatch(ivrsTreeDataResource.actions.fetchStarted({}));

      await gateways.ivrs.addIvr(newIvr);

      dispatch(ivrsTreeDataResource.actions.fetchSuccess({}));
      dispatch(ivrResource.actions.fetchSuccess(newIvr));
      dispatch(addSuccess(newIvr));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(addRequestError(err));
      }
      dispatch(toastError(`There was an error while adding`));
    }
  };

const saveLocations = async (ivrId: string, ivrVersion: number, ivrNodes: IvrNode[], positions, { gateways }) => {
  // remove not existed/deleted nodes position
  const existingPositions = reduce(
    positions,
    (acc, value, nodeId) => {
      const existingNode = find(ivrNodes, node => node.ivrNode.id === nodeId || START_NODE_ID === nodeId);
      if (existingNode) {
        acc[nodeId] = value;
      }

      return acc;
    },
    {}
  );

  await gateways.ivrsTreeData.replace(
    { id: ivrId, version: ivrVersion + 1 },
    { ivr_id: ivrId, ivr_version: ivrVersion + 1, positions: existingPositions }
  );
};

const createNewVersion: sharedTypes.AppThunk =
  (params: any, newIvr: Ivr) =>
  async (dispatch, getState, { gateways }) => {
    try {
      const { value: positions } = getState().ivrsTreeData;
      dispatch(addStarted({}));
      dispatch(ivrResource.actions.updateStarted({}));
      dispatch(ivrsTreeDataResource.actions.updateStarted({}));

      dispatch(ivrResource.actions.fetchStarted({}));

      const treeData: any = convertCoordinatesInTreeDataToString({ positions });

      const response = await gateways.ivrs.addVersion(params, newIvr);
      await saveLocations(newIvr.id, newIvr.version, newIvr.nodes!, treeData.positions, { gateways });

      dispatch(ivrsTreeDataResource.actions.fetchSuccess(cloneDeep(positions)));
      dispatch(ivrResource.actions.fetchSuccess(response));

      dispatch(ivrResource.actions.updateSuccess({}));
      dispatch(ivrsTreeDataResource.actions.updateSuccess({}));
      dispatch(addSuccess(response));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(addRequestError(err));
      }
      dispatch(toastError(`There was an error while adding`));
    }
  };

const restoreVersion: sharedTypes.AppThunk =
  (params: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(restoreVersionStarted({}));
      const result = await gateways.ivrs.restore({ id: params.id, version: params.version });
      const state = getState();

      dispatch(ivrResource.actions.fetchSuccess(produce(state.ivr.value, draft => ({ ...draft, ...result }))));

      dispatch(restoreVersionSuccess(result));

      return true;
    } catch (err) {
      dispatch(toastError(`There was an error while restoring`));
      dispatch(restoreVersionError(translateErrors((err as any).errors)));
      return false;
    }
  };

const renameIvr: sharedTypes.AppThunk =
  (params: any, attrs: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(updateStarted({}));
      await gateways.ivrs.update(params, attrs);
      dispatch(renameSuccess({ id: get(params, 'id'), name: get(attrs, 'name') }));

      dispatch(toastSuccess('Rename successful!'));
    } catch (err) {
      dispatch(toastError(`There was an error while renaming`));
      if (err instanceof StandardDomainError) {
        dispatch(addError(translateErrors(err.errors)));
        return;
      }
      if (err instanceof Unauthorized) {
        dispatch(logout());
        return;
      }
      dispatch(addRequestError(err));
    }
  };

const copyIvr: sharedTypes.AppThunk =
  (params: any, attrs: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(addStarted({}));
      const ivr = await gateways.ivrs.fetchVersion(params);
      const newIvr = produce(ivr, draft => {
        draft.id = attrs.id;
        draft.name = attrs.name;
        draft.description = attrs.name;
        draft.version = attrs.version;
      });
      await gateways.ivrs.addIvr(newIvr);
      dispatch(addSuccess(newIvr));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(addRequestError(err));
      }
      dispatch(toastError(`There was an error while copying`));
    }
  };

const archiveIvr: sharedTypes.AppThunk =
  (params: any, attrs: any) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(updateStarted({}));
      await gateways.ivrs.update(params, attrs);
      dispatch(archiveSuccess({ id: get(params, 'id'), archivedAt: get(attrs, 'archivedAt') }));

      dispatch(toastSuccess(params.toastSuccess));
    } catch (err) {
      dispatch(toastError(params.toastError));
      if (err instanceof StandardDomainError) {
        dispatch(addError(translateErrors(err.errors)));
        return;
      }
      if (err instanceof Unauthorized) {
        dispatch(logout());
        return;
      }
      dispatch(addRequestError(err));
    }
  };

const addNodesToConfiguration: sharedTypes.AppThunk =
  ({ attr, parentId, nodeType }: { attr: string; parentId: string; nodeType: NODE_TYPES }) =>
  async (dispatch, getState) => {
    const { value: ivr } = getState().ivr;
    const { value: positions } = getState().ivrsTreeData;

    const { config, treeData, newNodeId } = addNewNodeToConfiguration({
      attr,
      ivr: ivr as IvrConfiguration,
      nodeType,
      parentId,
      positions,
    });

    await dispatch(updateWithCoordinates({ config, positions: treeData.positions }));
    dispatch(ivrResource.actions.setSelectedNodeId(newNodeId));
  };

const addTreeFromTemplateToConfiguration: sharedTypes.AppThunk =
  ({ attr, parentId, templateId }: { attr: string; parentId: string; templateId: string }) =>
  async (dispatch, getState) => {
    const { value: ivr } = getState().ivr;
    const { value: organization } = getState().organization;
    const { value: locationState } = getState().ivrsTreeData;

    const { positions, config } = insertTemplate({
      attr,
      config: ivr,
      locationState,
      parentId,
      templateId,
      organization,
    });
    const newNodeId = (config as IvrConfiguration).rootNodeId;

    await dispatch(updateWithCoordinates({ config, positions }));
    dispatch(ivrResource.actions.setSelectedNodeId(newNodeId));
  };

const updateWithCoordinates: sharedTypes.AppThunk =
  ({ config, positions }) =>
  async (dispatch, _, { gateways }) => {
    try {
      dispatch(ivrsResource.actions.addStarted({}));
      dispatch(ivrResource.actions.updateStarted({}));
      dispatch(ivrResource.actions.fetchStarted({}));
      dispatch(ivrsTreeDataResource.actions.fetchStarted({}));

      const newConfig = produce<IvrConfiguration>(config, draft => {
        if (has(draft, 'nodesMap')) {
          draft.nodes = map(config.nodesMap, nd => {
            const node = cloneDeep(nd);
            if ([NODE_TYPES.IVR_NODE_DIAL, NODE_TYPES.IVR_NODE_VOICEMAIL].includes(node.type)) {
              node.ivrNode.name = node.ivrNode.description || node.ivrNode.selection || node.ivrNode.id;
            }
            return node;
          });
          delete draft.nodesMap;
        }
      });

      const { positions: transformedPositions } = convertCoordinatesInTreeDataToString({ positions });

      await saveLocations(
        newConfig.id,
        newConfig.version,
        (newConfig as IvrConfiguration).nodes,
        transformedPositions,
        {
          gateways,
        }
      );
      const response = await gateways.ivrs.addVersion({ id: newConfig.id, version: newConfig.version }, newConfig);

      dispatch(ivrsTreeDataResource.actions.fetchSuccess(cloneDeep(positions)));
      dispatch(addSuccess(response));
      dispatch(ivrResource.actions.updateSuccess({}));
      dispatch(ivrResource.actions.fetchSuccess(response));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(addRequestError(err));
      }
      dispatch(toastError(`There was an error while adding`));
    }
  };

ivrsResource.effects.addNodesToConfiguration = addNodesToConfiguration;
ivrsResource.effects.addTreeFromTemplateToConfiguration = addTreeFromTemplateToConfiguration;
ivrsResource.effects.archiveIvr = archiveIvr;
ivrsResource.effects.copyIvr = copyIvr;
ivrsResource.effects.createNewIvr = createNewIvr;
ivrsResource.effects.createNewVersion = createNewVersion;
ivrsResource.effects.renameIvr = renameIvr;
ivrsResource.effects.restoreVersion = restoreVersion;
ivrsResource.effects.updateWithCoordinates = updateWithCoordinates;

const {
  addError,
  addRequestError,
  addStarted,
  addSuccess,
  archiveSuccess,
  renameSuccess,
  restoreVersionError,
  restoreVersionStarted,
  restoreVersionSuccess,
  updateStarted,
} = ivrsResource.actions;

export default ivrsResource;
